<template>
  <div class="auth-container">
    <div class="auth-card">
      <!-- Logo -->
      <div class="logo-container">
        <router-link to="/" class="logo-main">
          <img src="/logo-no-bg.png" alt="BlogDavid Logo" class="logo-image" />
        </router-link>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="state-content">
        <div class="loading-spinner"></div>
        <h2 class="state-title">Verificando seu email...</h2>
        <p class="state-message">Por favor, aguarde um momento.</p>
      </div>

      <!-- Success State -->
      <div v-else-if="success" class="state-content">
        <div class="success-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>

        <h2 class="state-title">
          {{
            alreadyVerified
              ? "Email Já Verificado!"
              : "Email Verificado com Sucesso!"
          }}
        </h2>

        <p class="state-message">
          {{
            message ||
            "Sua conta foi ativada. Agora você pode fazer login e aproveitar todos os recursos do BlogDavid!"
          }}
        </p>

        <div class="action-buttons">
          <router-link to="/login" class="btn-primary">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
              />
            </svg>
            Fazer Login
          </router-link>

          <router-link to="/" class="btn-secondary">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            Voltar para Home
          </router-link>
        </div>
      </div>

      <!-- Error State -->
      <div v-else class="state-content">
        <div class="error-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>

        <h2 class="state-title">Erro na Verificação</h2>

        <p class="state-message">{{ errorMessage }}</p>

        <div class="action-buttons">
          <button
            @click="resendEmail"
            :disabled="resending"
            class="btn-primary"
          >
            <svg
              v-if="!resending"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <div v-else class="spinner"></div>
            {{ resending ? "Enviando..." : "Reenviar Email de Verificação" }}
          </button>

          <router-link to="/register" class="btn-secondary">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
              />
            </svg>
            Criar Nova Conta
          </router-link>

          <router-link to="/" class="btn-text"> Voltar para Home </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useAuthStore } from "@/store/auth";
import { useToast } from "vue-toastification";

export default {
  name: "EmailVerification",
  setup() {
    const route = useRoute();
    const authStore = useAuthStore();
    const toast = useToast();

    const loading = ref(true);
    const success = ref(false);
    const alreadyVerified = ref(false);
    const errorMessage = ref("");
    const message = ref("");
    const resending = ref(false);
    const userEmail = ref("");

    const verifyEmail = async () => {
      loading.value = true;
      const token = route.params.token;

      if (!token) {
        loading.value = false;
        success.value = false;
        errorMessage.value = "Token de verificação não encontrado na URL.";
        return;
      }

      try {
        const result = await authStore.verifyEmail(token);

        loading.value = false;

        if (result.success) {
          success.value = true;
          alreadyVerified.value = result.alreadyVerified || false;
          message.value = result.message;
          userEmail.value = result.email || "";

          if (!alreadyVerified.value) {
            toast.success("Email verificado com sucesso!");
          }
        } else {
          success.value = false;
          errorMessage.value =
            result.message ||
            "Erro ao verificar email. O link pode estar expirado.";
        }
      } catch (error) {
        loading.value = false;
        success.value = false;
        errorMessage.value =
          "Erro ao verificar email. O link pode estar expirado ou inválido.";
        console.error("Erro na verificação:", error);
      }
    };

    const resendEmail = async () => {
      if (!userEmail.value) {
        toast.error("Email não disponível para reenvio");
        return;
      }

      resending.value = true;

      try {
        await authStore.resendVerification(userEmail.value);
        toast.success(
          "Email de verificação reenviado! Verifique sua caixa de entrada."
        );
      } catch (error) {
        toast.error("Erro ao reenviar email. Tente novamente mais tarde.");
      } finally {
        resending.value = false;
      }
    };

    onMounted(() => {
      verifyEmail();
    });

    return {
      loading,
      success,
      alreadyVerified,
      errorMessage,
      message,
      resending,
      resendEmail,
    };
  },
};
</script>

<style scoped>
.auth-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  padding: 2rem 1rem;
}

.auth-card {
  width: 100%;
  max-width: 440px;
  background: white;
  border-radius: 16px;
  padding: 3rem 2.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Logo */
.logo-container {
  text-align: center;
  margin-bottom: 2rem;
}

.logo-main {
  display: inline-block;
  transition: transform 0.2s;
}

.logo-main:hover {
  transform: scale(1.05);
}

.logo-image {
  height: 60px;
  width: auto;
  object-fit: contain;
}

.state-content {
  text-align: center;
}

/* Loading State */
.loading-spinner {
  width: 64px;
  height: 64px;
  margin: 0 auto 1.5rem;
  border: 4px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Success State */
.success-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: #3b82f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  animation: scaleIn 0.5s ease-out;
}

.success-icon svg {
  width: 44px;
  height: 44px;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* Error State */
.error-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: #ef4444;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  animation: shake 0.5s ease-out;
}

.error-icon svg {
  width: 44px;
  height: 44px;
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-10px);
  }
  75% {
    transform: translateX(10px);
  }
}

.state-title {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 1rem;
}

.state-message {
  font-size: 1rem;
  color: #64748b;
  margin-bottom: 2rem;
  line-height: 1.6;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.btn-primary {
  width: 100%;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 600;
  color: white;
  background: #3b82f6;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-decoration: none;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-primary svg {
  width: 20px;
  height: 20px;
}

.btn-secondary {
  width: 100%;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 600;
  color: #1a202c;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-decoration: none;
}

.btn-secondary:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  transform: translateY(-2px);
}

.btn-secondary svg {
  width: 20px;
  height: 20px;
}

.btn-text {
  width: 100%;
  padding: 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.2s;
  text-decoration: none;
  display: inline-block;
  text-align: center;
}

.btn-text:hover {
  color: #1a202c;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

/* Responsive */
@media (max-width: 480px) {
  .auth-card {
    padding: 2rem 1.5rem;
  }

  .state-title {
    font-size: 1.5rem;
  }
}
</style>
